import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { toast } from "sonner";
import { Edit2, Trash2, Plus, Loader2, Car, Search } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
function absUrl(u) {
  if (!u) return u;
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith("/")) return `${BACKEND}${u}`;
  return u;
}

export default function AdminDashboard() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/vehicles");
      setVehicles(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const del = async (id, label) => {
    if (!window.confirm(`Eliminare definitivamente "${label}"?`)) return;
    try {
      await api.delete(`/vehicles/${id}`);
      toast.success("Veicolo eliminato");
      setVehicles((v) => v.filter((x) => x.id !== id));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const filtered = vehicles.filter((v) => {
    if (typeFilter !== "all" && v.type !== typeFilter) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      v.brand.toLowerCase().includes(s) ||
      v.model.toLowerCase().includes(s) ||
      v.fuel.toLowerCase().includes(s)
    );
  });

  const counts = {
    all: vehicles.length,
    scooter_cabinato: vehicles.filter((v) => v.type === "scooter_cabinato").length,
    auto_usata: vehicles.filter((v) => v.type === "auto_usata").length,
  };

  return (
    <AdminLayout>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-8">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#1e293b]">
            Gestione veicoli
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {vehicles.length} veicol{vehicles.length === 1 ? "o" : "i"} in vetrina · {filtered.length} visualizzat{filtered.length === 1 ? "o" : "i"}
          </p>
        </div>
        <Link
          to="/admin/vehicle/new"
          data-testid="dashboard-new-vehicle"
          className="hidden md:inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#76BA1B] text-white text-sm font-bold hover:bg-[#5fa015] hover:shadow-[0_8px_24px_rgba(118,186,27,0.45)] transition"
        >
          <Plus size={16} /> Nuovo veicolo
        </Link>
      </div>

      {/* Filters & search */}
      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="admin-search"
            placeholder="Cerca marca, modello, alimentazione…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-3 rounded-xl bg-white border border-slate-200 text-sm text-[#1e293b] placeholder-slate-400 focus:border-[#76BA1B] focus:shadow-[0_0_0_4px_rgba(118,186,27,0.12)] outline-none transition"
          />
        </div>
        <div className="flex gap-2 p-1.5 rounded-xl bg-white border border-slate-200">
          {[
            { k: "all", l: "Tutti" },
            { k: "scooter_cabinato", l: "Scooter" },
            { k: "auto_usata", l: "Auto" },
          ].map(({ k, l }) => {
            const active = typeFilter === k;
            return (
              <button
                key={k}
                onClick={() => setTypeFilter(k)}
                data-testid={`admin-filter-${k}`}
                className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition ${
                  active
                    ? "bg-[#76BA1B] text-white"
                    : "text-slate-500 hover:bg-slate-100"
                }`}
              >
                {l} <span className="ml-1 opacity-70">{counts[k]}</span>
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-slate-400">
          <Loader2 className="animate-spin mr-3" /> Caricamento…
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl p-16 text-center border border-dashed border-slate-200">
          <Car className="mx-auto text-slate-300 mb-4" size={32} />
          <div className="text-slate-500 mb-5">
            {search || typeFilter !== "all"
              ? "Nessun veicolo corrisponde ai filtri."
              : "Nessun veicolo. Inizia aggiungendone uno."}
          </div>
          {!search && typeFilter === "all" && (
            <Link
              to="/admin/vehicle/new"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#76BA1B] text-white text-sm font-bold hover:bg-[#5fa015] transition"
            >
              <Plus size={14} /> Aggiungi veicolo
            </Link>
          )}
        </div>
      ) : (
        <div
          data-testid="admin-vehicle-list"
          className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5"
        >
          {filtered.map((v) => (
            <div
              key={v.id}
              data-testid={`admin-vehicle-row-${v.id}`}
              className="bg-white rounded-2xl overflow-hidden border border-slate-200 hover:border-[#76BA1B]/50 hover:shadow-[0_10px_30px_rgba(118,186,27,0.12)] transition"
            >
              <div className="aspect-[16/10] bg-slate-100 overflow-hidden relative">
                {v.images?.[0] ? (
                  <img
                    src={absUrl(v.images[0])}
                    alt={v.model}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-300">
                    <Car size={32} />
                  </div>
                )}
                <span
                  className={`absolute top-3 left-3 px-2.5 py-1 rounded-md text-[9px] font-bold uppercase tracking-wider ${
                    v.type === "scooter_cabinato"
                      ? "bg-[#76BA1B] text-white"
                      : "bg-white/95 text-[#1e293b] backdrop-blur"
                  }`}
                >
                  {v.type === "scooter_cabinato" ? "Scooter" : "Auto"}
                </span>
                {v.images?.length > 1 && (
                  <span className="absolute top-3 right-3 px-2 py-1 rounded-md text-[10px] font-bold bg-white/95 text-[#1e293b] backdrop-blur">
                    {v.images.length} foto
                  </span>
                )}
              </div>
              <div className="p-4">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                  {v.brand}
                </div>
                <div className="font-heading text-base font-bold text-[#1e293b] truncate">
                  {v.model}
                </div>
                <div className="mt-3 flex items-baseline justify-between">
                  <div className="text-xl font-bold text-[#76BA1B]">
                    € {new Intl.NumberFormat("it-IT").format(v.price)}
                  </div>
                  <div className="text-xs text-slate-500">
                    {v.year} · {new Intl.NumberFormat("it-IT").format(v.km)} km
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-slate-100 flex gap-2">
                  <Link
                    to={`/admin/vehicle/edit/${v.id}`}
                    data-testid={`admin-edit-${v.id}`}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg bg-slate-100 hover:bg-[#76BA1B] hover:text-white text-xs font-bold text-[#1e293b] transition"
                  >
                    <Edit2 size={12} /> Modifica
                  </Link>
                  <button
                    data-testid={`admin-delete-${v.id}`}
                    onClick={() => del(v.id, `${v.brand} ${v.model}`)}
                    className="flex items-center justify-center px-3 py-2.5 rounded-lg bg-red-50 text-red-500 text-xs hover:bg-red-100 transition"
                    aria-label="Elimina"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
